import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CreditCard, ArrowLeft, Download, Share2, Copy, Check, 
  Phone, Mail, Globe, MapPin, Linkedin, Instagram, 
  Twitter, MessageCircle, QrCode, User, Building, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { useAnalytics } from '../../utils/useAnalytics';

const BusinessCardGenerator = () => {
  const cardRef = useRef(null);
  const [card, setCard] = useState({
    name: '',
    title: '',
    company: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    linkedin: '',
    instagram: '',
    twitter: '',
    whatsapp: ''
  });
  const [theme, setTheme] = useState('dark');
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(true);
  const { trackView, trackGenerate, trackDownload, trackShare } = useAnalytics('business-card');

  // Track page view
  useEffect(() => {
    trackView();
  }, [trackView]);

  const themes = {
    dark: {
      bg: 'bg-gradient-to-br from-gray-900 to-gray-800',
      text: 'text-white',
      accent: 'text-primary',
      subtext: 'text-gray-400'
    },
    light: {
      bg: 'bg-gradient-to-br from-white to-gray-100',
      text: 'text-gray-900',
      accent: 'text-blue-600',
      subtext: 'text-gray-500'
    },
    blue: {
      bg: 'bg-gradient-to-br from-blue-600 to-blue-800',
      text: 'text-white',
      accent: 'text-blue-200',
      subtext: 'text-blue-200/70'
    },
    gold: {
      bg: 'bg-gradient-to-br from-yellow-600 to-amber-800',
      text: 'text-white',
      accent: 'text-yellow-200',
      subtext: 'text-yellow-200/70'
    },
    green: {
      bg: 'bg-gradient-to-br from-emerald-600 to-teal-800',
      text: 'text-white',
      accent: 'text-emerald-200',
      subtext: 'text-emerald-200/70'
    },
    purple: {
      bg: 'bg-gradient-to-br from-purple-600 to-pink-700',
      text: 'text-white',
      accent: 'text-purple-200',
      subtext: 'text-purple-200/70'
    }
  };

  const currentTheme = themes[theme];

  const generateVCard = () => {
    return `BEGIN:VCARD
VERSION:3.0
N:${card.name}
FN:${card.name}
TITLE:${card.title}
ORG:${card.company}
TEL:${card.phone}
EMAIL:${card.email}
URL:${card.website}
ADR:;;${card.address};;;
END:VCARD`;
  };

  const getQRUrl = () => {
    const vcard = encodeURIComponent(generateVCard());
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${vcard}&color=000000&bgcolor=ffffff`;
  };

  const copyVCard = async () => {
    try {
      await navigator.clipboard.writeText(generateVCard());
      setCopied(true);
      trackGenerate({ theme, hasQR: showQR });
      toast.success('vCard copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const downloadCard = () => {
    const cardElement = cardRef.current;
    if (!cardElement) return;

    // For simplicity, we'll generate HTML that can be saved
    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>${card.name || 'Business Card'}</title>
<link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
</head>
<body class="p-8 bg-gray-100 min-h-screen flex items-center justify-center">
<div class="${currentTheme.bg} rounded-2xl p-8 shadow-2xl max-w-md w-full">
  <div class="flex items-start justify-between">
    <div>
      <h1 class="${currentTheme.text} text-2xl font-bold">${card.name || 'Your Name'}</h1>
      <p class="${currentTheme.accent} font-medium">${card.title || 'Your Title'}</p>
      <p class="${currentTheme.subtext} text-sm">${card.company || 'Company Name'}</p>
    </div>
    ${showQR ? `<img src="${getQRUrl()}" class="w-20 h-20 rounded-lg" alt="QR Code">` : ''}
  </div>
  <div class="mt-6 space-y-2 ${currentTheme.subtext} text-sm">
    ${card.phone ? `<p>📱 ${card.phone}</p>` : ''}
    ${card.email ? `<p>✉️ ${card.email}</p>` : ''}
    ${card.website ? `<p>🌐 ${card.website}</p>` : ''}
    ${card.address ? `<p>📍 ${card.address}</p>` : ''}
  </div>
  <div class="mt-4 flex gap-3">
    ${card.linkedin ? `<a href="${card.linkedin}" class="${currentTheme.accent}">LinkedIn</a>` : ''}
    ${card.instagram ? `<a href="${card.instagram}" class="${currentTheme.accent}">Instagram</a>` : ''}
    ${card.whatsapp ? `<a href="https://wa.me/${card.whatsapp}" class="${currentTheme.accent}">WhatsApp</a>` : ''}
  </div>
</div>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${card.name?.toLowerCase().replace(/\s+/g, '-') || 'business-card'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    trackDownload({ theme });
    toast.success('Card downloaded!');
  };

  const shareCard = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${card.name}'s Business Card`,
          text: `Contact: ${card.name}\n${card.title} at ${card.company}\n📱 ${card.phone}\n✉️ ${card.email}`,
          url: card.website
        });
        trackShare({ method: 'native' });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      copyVCard();
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="business-card-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500/20 via-background to-amber-500/10 py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <Link to="/tools" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Tools
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center">
              <CreditCard className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Digital Business Card</h1>
              <p className="text-muted-foreground">Create shareable digital business cards with QR</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form */}
          <div className="space-y-6">
            {/* Personal Info */}
            <div className="glass-card p-6 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Personal Information
              </h3>
              <input
                type="text"
                placeholder="Full Name"
                value={card.name}
                onChange={(e) => setCard({ ...card, name: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                data-testid="name-input"
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Job Title"
                  value={card.title}
                  onChange={(e) => setCard({ ...card, title: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
                <input
                  type="text"
                  placeholder="Company Name"
                  value={card.company}
                  onChange={(e) => setCard({ ...card, company: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="glass-card p-6 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Phone className="w-5 h-5 text-green-500" />
                Contact Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={card.phone}
                    onChange={(e) => setCard({ ...card, phone: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={card.email}
                    onChange={(e) => setCard({ ...card, email: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  />
                </div>
              </div>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="url"
                  placeholder="Website URL"
                  value={card.website}
                  onChange={(e) => setCard({ ...card, website: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <textarea
                  placeholder="Address"
                  value={card.address}
                  onChange={(e) => setCard({ ...card, address: e.target.value })}
                  rows={2}
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl resize-none"
                />
              </div>
            </div>

            {/* Social Links */}
            <div className="glass-card p-6 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Linkedin className="w-5 h-5 text-blue-500" />
                Social Links
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500" />
                  <input
                    type="url"
                    placeholder="LinkedIn URL"
                    value={card.linkedin}
                    onChange={(e) => setCard({ ...card, linkedin: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  />
                </div>
                <div className="relative">
                  <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pink-500" />
                  <input
                    type="url"
                    placeholder="Instagram URL"
                    value={card.instagram}
                    onChange={(e) => setCard({ ...card, instagram: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  />
                </div>
                <div className="relative">
                  <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-500" />
                  <input
                    type="url"
                    placeholder="Twitter URL"
                    value={card.twitter}
                    onChange={(e) => setCard({ ...card, twitter: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  />
                </div>
                <div className="relative">
                  <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                  <input
                    type="tel"
                    placeholder="WhatsApp Number"
                    value={card.whatsapp}
                    onChange={(e) => setCard({ ...card, whatsapp: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  />
                </div>
              </div>
            </div>

            {/* Theme & Options */}
            <div className="glass-card p-6">
              <h3 className="font-semibold mb-4">Theme & Options</h3>
              <div className="grid grid-cols-6 gap-2 mb-4">
                {Object.keys(themes).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTheme(t)}
                    className={`p-4 rounded-xl ${themes[t].bg} ${
                      theme === t ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''
                    }`}
                    title={t}
                  />
                ))}
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showQR}
                  onChange={(e) => setShowQR(e.target.checked)}
                  className="w-5 h-5 rounded accent-primary"
                />
                <span>Show QR Code on card</span>
              </label>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={downloadCard}
                className="flex-1 py-4 bg-amber-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-amber-600 transition-colors"
                data-testid="download-card-btn"
              >
                <Download className="w-5 h-5" />
                Download
              </button>
              <button
                onClick={shareCard}
                className="flex-1 py-4 bg-white/10 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
              >
                <Share2 className="w-5 h-5" />
                Share
              </button>
              <button
                onClick={copyVCard}
                className="py-4 px-6 bg-white/10 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
              >
                {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="lg:sticky lg:top-8">
            <div className="glass-card p-4 mb-4">
              <h3 className="font-semibold">Card Preview</h3>
            </div>
            
            <div 
              ref={cardRef}
              className={`${currentTheme.bg} rounded-2xl p-8 shadow-2xl`}
              data-testid="card-preview"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h2 className={`${currentTheme.text} text-2xl font-bold`}>
                    {card.name || 'Your Name'}
                  </h2>
                  <p className={`${currentTheme.accent} font-medium`}>
                    {card.title || 'Your Title'}
                  </p>
                  <p className={`${currentTheme.subtext} text-sm mt-1`}>
                    {card.company || 'Company Name'}
                  </p>
                </div>
                {showQR && (card.name || card.phone || card.email) && (
                  <img 
                    src={getQRUrl()} 
                    alt="QR Code" 
                    className="w-20 h-20 rounded-lg bg-white p-1"
                  />
                )}
              </div>

              <div className={`mt-6 space-y-2 ${currentTheme.subtext} text-sm`}>
                {card.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span>{card.phone}</span>
                  </div>
                )}
                {card.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <span>{card.email}</span>
                  </div>
                )}
                {card.website && (
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    <span>{card.website}</span>
                  </div>
                )}
                {card.address && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{card.address}</span>
                  </div>
                )}
              </div>

              {(card.linkedin || card.instagram || card.twitter || card.whatsapp) && (
                <div className={`mt-4 pt-4 border-t border-white/10 flex gap-3`}>
                  {card.linkedin && (
                    <a href={card.linkedin} className={`${currentTheme.accent} hover:opacity-80`}>
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                  {card.instagram && (
                    <a href={card.instagram} className={`${currentTheme.accent} hover:opacity-80`}>
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {card.twitter && (
                    <a href={card.twitter} className={`${currentTheme.accent} hover:opacity-80`}>
                      <Twitter className="w-5 h-5" />
                    </a>
                  )}
                  {card.whatsapp && (
                    <a href={`https://wa.me/${card.whatsapp}`} className={`${currentTheme.accent} hover:opacity-80`}>
                      <MessageCircle className="w-5 h-5" />
                    </a>
                  )}
                </div>
              )}
            </div>

            <p className="text-center text-sm text-muted-foreground mt-4">
              Scan QR to save contact instantly
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessCardGenerator;
